import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import { isMobile } from 'react-device-detect';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import SpaceResult from './results/space.js';
import Paginator from './results/paginator.js';
import SpaceNoResults from './results/space/noresults.js';
import Emitter from '@/helpers/emitter.js';
import SpaceLoading from './results/space/loading.js';
import HofBanner from './hofbanner.js';

function ResultsList({ spacesData }) {
  return (
    <>
      <div className="grid grid-cols-1 px-6 pt-4 pb-6 sm:grid-cols-2 xl:grid-cols-3 gap-7 lg:gap-x-6 lg:gap-y-7 lg:px-8 lg:pt-4 lg:pb-8">
        {/* La clé était `space.id`, or un fragment ref Relay n'expose que __id / __fragments :
            les 24 cartes avaient donc la même clé ("...undefined"), et React ne pouvait plus les
            distinguer d'une recherche à l'autre. __id = la ref du space (ex. S1803784). */}
        {(spacesData?.spaces || []).map((space, index) => (
          <SpaceResult
            // eslint-disable-next-line no-underscore-dangle
            key={`searchresultsspace${space?.__id || space?.id || index}`}
            item={space}
            // Les 3 premières cartes (1re rangée xl) sont au-dessus de la ligne de
            // flottaison : leur photo est le LCP de la page — elle doit se charger en
            // priorité, pas en lazy (LCP mesuré à 25,8 s à cause de ça).
            lcpPriority={index < 3}
          />
        ))}
      </div>
      <Paginator resume={spacesData?.resume} />
    </>
  );
}

// WEA-157 — classes de la colonne des résultats, IDENTIQUES pour les trois états (chargement,
// résultats, aucun résultat) : c'est ce qui évite que la zone "réductions" bouge à chaque
// recherche (avant, chaque état avait ses propres classes -> bordures qui apparaissent, ancêtre
// défilable qui change, donc barre sticky qui se recolle et accordeon qui se rejoue).
// overflow-x-hidden UNIQUEMENT en desktop (lg) : en mobile il faisait de cette colonne un
// conteneur de défilement au contenu non débordant, ce qui empêchait la barre de cases de se
// coller (sticky s'accroche au premier ancêtre défilable). En desktop c'est lui qui fait défiler
// la colonne : on le garde.
// WEA-166 y ajoute son ombre portée (le design v5 a retiré les bordures) : comme les trois états
// partagent cette seule chaîne, l'ombre ne peut pas apparaître/disparaître d'un état à l'autre.
const COLUMN_CLASSES = 'absolute top-0 z-10 w-full bg-white lg:overflow-x-hidden lg:rounded-tr-2xl lg:h-[calc(100vh-126px)] lg:relative md:col-span-7 lg:col-span-7 lg:shadow-[0_-12px_28px_-16px_rgba(19,14,27,0.16),0_1px_2px_rgba(19,14,27,0.03),0_20px_40px_-20px_rgba(19,14,27,0.14)]';

function Results({ space }) {
  const spacesData = useSearchZustandStore((state) => state.spacesData);
  const spacesDataDone = useSearchZustandStore((state) => state.spacesDataDone);
  const [isClientSide, setIsClientSide] = useState(false);
  const [currentSpaces, setCurrentSpaces] = useState(space || spacesData);
  // WEA-169 — quand le serveur a déjà rendu la grille (prop `space`), le client démarre
  // SANS état de chargement : même premier rendu que le SSR (sinon le squelette remplaçait
  // les 24 cartes serveur en attendant la requête bounds — grille jetée, LCP client-side).
  const [loading, setLoading] = useState(!space?.spaces?.length);

  const scrollToTop = useCallback((elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'instant', block: 'start' });
    }
  }, []);

  useEffect(() => {
    setIsClientSide(true);
    const spaceloadingsearchHandler = (value) => {
      setLoading(value);
    };

    Emitter.on('spaceloadingsearch', spaceloadingsearchHandler);
    return () => {
      Emitter.off('spaceloadingsearch', spaceloadingsearchHandler);
    };
  }, []);

  useEffect(() => {
    if (loading) {
      setCurrentSpaces([]);
    } else if (spacesData?.spaces) {
      // Un vrai jeu de résultats (recherche ou adoption SSR).
      setLoading(false);
      setCurrentSpaces(spacesData);
    } else if (Array.isArray(spacesData) && spacesDataDone && spacesDataDone !== 'ssr') {
      // Un vrai « 0 résultat » : events/space.js pose [] avec un done aléatoire (nanoid).
      setCurrentSpaces(spacesData);
    } else if (space?.spaces?.length) {
      /*
        WEA-176 — le store est dans un état de BOOT ('notready' par défaut, ou [] porteur
        de done==='ssr' quand le cleanup du double-montage StrictMode a vidé l'adoption en
        dev) alors que ce composant TIENT la grille SSR dans sa prop : on se répare
        soi-même — on ré-adopte dans le store ET à l'écran. C'est déterministe quel que
        soit l'ordre machine-dépendant des inits carte/filtres (le debounce de
        events/search.js ne garde que le dernier événement) : au pire, la grille revient
        d'ici. Un état issu d'une vraie recherche (done nanoid) ne passe jamais par cette
        branche.
      */
      useSearchZustandStore.setState({ spacesData: space, spacesDataDone: 'ssr' });
      setCurrentSpaces(space);
    }

    if (isMobile && document.getElementById('contentSearchList2256')) {
      scrollToTop('contentSearchList2256');
    }

    if (document.getElementById('contentSearchList2257')) {
      scrollToTop('contentSearchList2257');
    }
  }, [spacesData, spacesDataDone, loading, scrollToTop]);

  // Seul le CONTENU change d'un état à l'autre : la colonne, le conteneur de liste et le panneau
  // "réductions" restent montés, donc rien ne saute pendant qu'une recherche tourne.
  const renderContent = () => {
    if (isClientSide && (loading || !spacesDataDone)) return <SpaceLoading />;
    if (!loading && !currentSpaces?.spaces?.length) return <SpaceNoResults />;
    return <ResultsList spacesData={currentSpaces} />;
  };

  // Pas de <Suspense> ici : cette frontière recevait les changements d'état (squelette ->
  // résultats) AVANT la fin de l'hydratation, ce qui faisait basculer tout le bloc en rendu client
  // ("This Suspense boundary received an update before it finished hydrating"). La frontière
  // existante dans components/pages/search.js couvre déjà ce qui peut suspendre ici.
  return (
    <div className={COLUMN_CLASSES}>
      <div id="contentSearchList2257" className="bg-white">
        <HofBanner />
        {renderContent()}
      </div>
    </div>
  );
}

export default Results;
